import {
  DateRange,
  PickersShortcutsItem,
} from '@mui/x-date-pickers-pro';
import React, {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DispatchContext } from '../../contexts/DispatchContext';
import { useStateContext } from '../../hooks/useStateContext';
import { Entity } from '../../types/entities/Entity';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/hu';
import 'dayjs/locale/en-gb';
import { viewPeriod as viewPeriodService } from '../../services/viewPeriod';
import { debounce } from 'lodash';
import Tooltip from '@mui/material/Tooltip';
import { IconButton } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';

import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';

const addDays = (date: Date | null, days: number) => {
  if (date === null) {
    return null;
  }
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

const stepPeriod = (period: any, days: number): any => {
  return [addDays(period[0], days), addDays(period[1], days)];
};

type Props<T extends Entity> = {
  changed: (value?: boolean) => void;
};

export const PeriodSelector = <T extends Entity>({
  changed,
}: Props<T>): ReactElement => {
  const dispatch = useContext(DispatchContext);

  const { viewPeriod } = useStateContext();

  const isAllTimeSelected = viewPeriod.every((date:any) => date === null);

  const [value, setValue] = useState<any>([
    dayjs(viewPeriod[0]),
    dayjs(viewPeriod[1]),
  ]);

  const [openPopover, setOpenPopover] = useState<boolean>(false);

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: 'This Week',
      getValue: () => {
        const today = dayjs();
        const dayOfWeek = today.day();
        const weekStart = today.subtract(dayOfWeek - 1, 'day');
        const weekEnd = weekStart.add(6, 'day');
        return [weekStart, weekEnd];
      },
    },
    {
      label: 'Previous Week',
      getValue: () => {
        const today = dayjs();
        const dayOfWeek = today.day();
        const prevWeekStart = today.subtract(dayOfWeek + 6, 'day');
        const prevWeekEnd = prevWeekStart.add(6, 'day');
        return [prevWeekStart, prevWeekEnd];
      },
    },
    {
      label: 'Last 7 Days',
      getValue: () => {
        const today = dayjs();
        return [today.subtract(7, 'day'), today];
      },
    },
    {
      label: 'Current Month',
      getValue: () => {
        const today = dayjs();
        return [today.startOf('month'), today.endOf('month')];
      },
    },
    {
      label: 'Next Month',
      getValue: () => {
        const today = dayjs();
        const startOfNextMonth = today.endOf('month').add(1, 'day');
        return [startOfNextMonth, startOfNextMonth.endOf('month')];
      },
    },
    { label: 'Reset', getValue: () => [null, null] },
  ];

  const handleDebouncedOnClick = useCallback((newValue: any) => {
    changed(true);
    dispatch({
      type: 'SET_VIEW_PERIOD',
      payload: newValue,
    });
  }, []);

  const debouncedOnClick = useMemo(
    () => debounce(handleDebouncedOnClick, 100),
    [handleDebouncedOnClick]
  );

  return (
    <>
      <Tooltip title={'Move one week backward'} placement="bottom">
        <IconButton
          size={'small'}
          disabled={isAllTimeSelected}
          onClick={() => {
            const dispatchedValue = stepPeriod(value, -7);
            setValue([dayjs(dispatchedValue[0]), dayjs(dispatchedValue[1])]);
            debouncedOnClick(dispatchedValue);
            setOpenPopover(false);
          }}
          sx={{
            marginLeft: '0px !important',
          }}
        >
          <ChevronLeftRoundedIcon fontSize={'small'} />
        </IconButton>
      </Tooltip>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
        <DateRangePicker
          value={[value[0], value[1]]}
          slots={{ field: SingleInputDateRangeField }}
          slotProps={{
            toolbar: {
              hidden: true,
            },
            actionBar: {
              actions: [],
              hidden: true,
            },
            shortcuts: {
              items: shortcutsItems,
            },
          }}
          name="allowedRange"
          sx={{
            '.MuiInputBase-root': {
              fontSize: '0.875rem',
              minWidth: '180px',
              padding: '0px',
            },
            '& .MuiInputBase-input.MuiOutlinedInput-input':{
              height: '19px',
            },
            '.MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '50px',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.2)',
              },
              '& input': {
                textAlign: 'center',
                padding: '5px',
                fontSize: '11px'
              },
            },
          }}
          onChange={(newValue, context) => {
            if (newValue[0].isBefore(value[0]) && !context?.shortcut) {
              newValue[1] = null;
            }
            setValue([newValue[0], newValue[1]]);

            if (newValue[0] && newValue[1]) {
              const start = new Date(dayjs(newValue[0]).format('YYYY-MM-DD'));
              const end = new Date(dayjs(newValue[1]).format('YYYY-MM-DD'));
              dispatch({ type: 'SET_VIEW_PERIOD', payload: [start, end] });
              changed(true);
              setOpenPopover(!openPopover);
            }
          }}
          format="YYYY-MM-DD"
        />
      </LocalizationProvider>

      <Tooltip title={'Move one week forward'} placement="bottom">
        <IconButton
          sx={{
            marginLeft: '0px !important',
          }}
          size={'small'}
          disabled={isAllTimeSelected}
          onClick={() => {
            const dispatchedValue = stepPeriod(value, 7);
            setValue([dayjs(dispatchedValue[0]), dayjs(dispatchedValue[1])]);
            debouncedOnClick(dispatchedValue);
            setOpenPopover(false);
          }}
        >
          <ChevronRightRoundedIcon fontSize={'small'} />
        </IconButton>
      </Tooltip>
      <Tooltip title={'Show current week'} placement="bottom">
        <IconButton
          sx={{
            marginLeft: '0px !important',
          }}
          size={'small'}
          onClick={() => {
            changed(true);
            dispatch({ type: 'RESET_VIEW_PERIOD' });
            const dispatchedValue = viewPeriodService.getDefault();
            setValue([dayjs(dispatchedValue[0]), dayjs(dispatchedValue[1])]);
            setOpenPopover(false);
          }}
        >
          <CalendarMonthRoundedIcon fontSize={'small'} />
        </IconButton>
      </Tooltip>

      <Tooltip title={'Show all time'} placement="bottom">
        <IconButton
          sx={{
            marginLeft: '0px !important',
          }}
          size={'small'}
          disabled={isAllTimeSelected}
          onClick={() => {
            changed(true);
            dispatch({ type: 'SET_VIEW_PERIOD', payload: [null, null] });
            setValue([null, null]);
            setOpenPopover(false);
          }}
        >
          <AccessTimeRoundedIcon fontSize={'small'} />
        </IconButton>
      </Tooltip>
    </>
  );
};
